import { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAccessDecision } from '@/hooks/useAccessDecision';
import { useSubscription } from '@/hooks/useSubscription';
import { FeatureFlag, ROUTE_FEATURE_MAP } from '@/lib/subscriptionTiers';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Lock, ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@/hooks/useTranslation';
import { ROUTES } from '@/lib/routes';

interface FeatureRouteGuardProps {
  feature?: FeatureFlag;
  requiredRole?: string;
  adminOnly?: boolean;
  children: ReactNode;
}

export function FeatureRouteGuard({ feature, requiredRole, adminOnly, children }: FeatureRouteGuardProps) {
  const { checkAccess, executeCta } = useAccessDecision();
  const { loading: subscriptionLoading } = useSubscription();
  const navigate = useNavigate();
  const { translate: t } = useTranslation();

  // Block rendering until subscription data is resolved to prevent flash of gated content
  if (subscriptionLoading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const decision = checkAccess({ feature, requiredRole, adminOnly });

  if (decision.allowed) {
    return <>{children}</>;
  }

  return (
    <div className="container mx-auto py-12 px-4 max-w-lg">
      <Card className="border-dashed border-2 border-muted">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
            <Lock className="h-8 w-8 text-muted-foreground" />
          </div>
          <CardTitle className="text-xl">{t("feature_guard_access_limited")}</CardTitle>
          <CardDescription className="text-base mt-2">
            {decision.message}
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          {decision.ctaAction !== 'none' && (
            <Button
              onClick={() => executeCta(decision.ctaAction, decision)}
              className="gap-2"
              size="lg"
            >
              {decision.ctaLabel}
              <ArrowRight className="h-4 w-4" />
            </Button>
          )}
          <div className="pt-2">
            <Button
              variant="ghost"
              onClick={() => navigate(ROUTES.DASHBOARD)}
              className="gap-2 text-muted-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              {t("feature_guard_back_dashboard")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Route-level configuration for tier + role + admin gating.
 * Extend this map for routes needing role/admin checks beyond ROUTE_FEATURE_MAP.
 */
const ROUTE_ROLE_MAP: Record<string, { requiredRole?: string; adminOnly?: boolean }> = {
  // Note: RTP role gating is handled by App.tsx RouteGuards directly.
  // Only add entries here if using AutoFeatureGuard wrapper.
};

/**
 * Automatically guards routes based on ROUTE_FEATURE_MAP + ROUTE_ROLE_MAP.
 * Use this as a wrapper component in App.tsx.
 */
export function AutoFeatureGuard({ children }: { children: ReactNode }) {
  const location = useLocation();
  const feature = ROUTE_FEATURE_MAP[location.pathname];
  const roleConfig = ROUTE_ROLE_MAP[location.pathname];

  // If no gating config at all, pass through
  if (!feature && !roleConfig) {
    return <>{children}</>;
  }

  return (
    <FeatureRouteGuard
      feature={feature}
      requiredRole={roleConfig?.requiredRole}
      adminOnly={roleConfig?.adminOnly}
    >
      {children}
    </FeatureRouteGuard>
  );
}
